import { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw, Server, Network as NetworkIcon, Zap, ArrowDownToLine, ArrowUpFromLine, AlertCircle } from 'lucide-react';
import Tabs, { type Tab } from '../components/common/Tabs';
import DataTable, { type Column } from '../components/common/DataTable';
import StatCard from '../components/common/StatCard';
import { getBGPStatus, getOSPFStatus, getBFDStatus, type BGPPeer, type OSPFNeighbor, type BFDSession } from '../services/routingStatus.service';
import edgeService, { type Edge } from '../services/edge.service';

const Routing = () => {
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedEdge, setSelectedEdge] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Routing data state
  const [bgpPeers, setBgpPeers] = useState<BGPPeer[]>([]);
  const [ospfNeighbors, setOspfNeighbors] = useState<OSPFNeighbor[]>([]);
  const [bfdSessions, setBfdSessions] = useState<BFDSession[]>([]);

  const selectedEdgeData = edges.find((e) => e.id === selectedEdge);
  const selectedEdgeName = selectedEdgeData?.devName || selectedEdgeData?.devSerNum || 'Select an edge';

  // Fetch edges on mount
  useEffect(() => {
    const fetchEdges = async () => {
      try {
        const response = await edgeService.list();
        const edgeList = response?.items || [];
        setEdges(edgeList);
        // Auto-select first edge if available
        if (edgeList.length > 0 && !selectedEdge) {
          setSelectedEdge(edgeList[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch edges:', err);
      }
    };
    fetchEdges();
  }, []);

  // Fetch routing data when edge is selected
  const fetchRoutingData = useCallback(async () => {
    if (!selectedEdge) return;

    setLoading(true);
    setError(null);

    try {
      const [bgp, ospf, bfd] = await Promise.all([
        getBGPStatus(selectedEdge),
        getOSPFStatus(selectedEdge),
        getBFDStatus(selectedEdge),
      ]);

      setBgpPeers(bgp);
      setOspfNeighbors(ospf);
      setBfdSessions(bfd);
    } catch (err) {
      console.error('Failed to fetch routing data:', err);
      setError('Failed to fetch routing data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedEdge]);

  useEffect(() => {
    fetchRoutingData();
  }, [fetchRoutingData]);

  // BGP Tab
  const bgpColumns: Column<BGPPeer>[] = [
    { key: 'peerIp', label: 'Peer IP', sortable: true, render: (row) => <span className="font-mono">{row.peerIp}</span> },
    { key: 'asn', label: 'ASN', sortable: true },
    {
      key: 'state',
      label: 'State',
      sortable: true,
      render: (row) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.state === 'Established' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {row.state}
        </span>
      ),
    },
    { key: 'uptime', label: 'Uptime', sortable: true },
    { key: 'prefixReceived', label: 'Prefixes Received', sortable: true },
    { key: 'prefixSent', label: 'Prefixes Sent', sortable: true },
    { key: 'lastUpdate', label: 'Last Update' },
  ];

  const bgpTab = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={Server}
          title="Total Peers"
          value={bgpPeers.length}
          color="primary"
        />
        <StatCard
          icon={Activity}
          title="Established"
          value={bgpPeers.filter((p) => p.state === 'Established').length}
          color="success"
        />
        <StatCard
          icon={ArrowDownToLine}
          title="Total Prefixes RX"
          value={bgpPeers.reduce((sum, p) => sum + (p.prefixReceived || 0), 0)}
          color="info"
        />
        <StatCard
          icon={ArrowUpFromLine}
          title="Total Prefixes TX"
          value={bgpPeers.reduce((sum, p) => sum + (p.prefixSent || 0), 0)}
          color="secondary"
        />
      </div>

      {bgpPeers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <NetworkIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>No BGP peers configured for this edge</p>
        </div>
      ) : (
        <DataTable columns={bgpColumns} data={bgpPeers} searchPlaceholder="Search BGP peers..." />
      )}
    </div>
  );

  // OSPF Tab
  const ospfColumns: Column<OSPFNeighbor>[] = [
    { key: 'neighborId', label: 'Neighbor ID', sortable: true, render: (row) => <span className="font-mono">{row.neighborId}</span> },
    { key: 'interface', label: 'Interface', sortable: true },
    {
      key: 'state',
      label: 'State',
      sortable: true,
      render: (row) => {
        const isActive = row.state === 'Full' || !row.state?.toLowerCase().includes('no ospf');
        return (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              isActive ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}
          >
            {row.state}
          </span>
        );
      },
    },
    { key: 'priority', label: 'Priority', sortable: true },
    { key: 'deadTime', label: 'Dead Time', render: (row) => <span className="font-mono">{row.deadTime}</span> },
    { key: 'address', label: 'Address', render: (row) => <span className="font-mono">{row.address}</span> },
  ];

  const ospfTab = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={Server}
          title="Total Neighbors"
          value={ospfNeighbors.length}
          color="primary"
        />
        <StatCard
          icon={Activity}
          title="Full State"
          value={ospfNeighbors.filter((n) => n.state === 'Full').length}
          color="success"
        />
        <StatCard
          icon={NetworkIcon}
          title="Interfaces"
          value={new Set(ospfNeighbors.map((n) => n.interface)).size}
          color="info"
        />
      </div>

      {ospfNeighbors.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <NetworkIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>No OSPF neighbors configured for this edge</p>
        </div>
      ) : (
        <DataTable columns={ospfColumns} data={ospfNeighbors} searchPlaceholder="Search OSPF neighbors..." />
      )}
    </div>
  );

  // BFD Tab
  const bfdColumns: Column<BFDSession>[] = [
    { key: 'peer', label: 'Peer', sortable: true, render: (row) => <span className="font-mono">{row.peer}</span> },
    { key: 'interface', label: 'Interface', sortable: true },
    {
      key: 'state',
      label: 'State',
      sortable: true,
      render: (row) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.state === 'Up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {row.state}
        </span>
      ),
    },
    { key: 'interval', label: 'Interval', sortable: true },
    { key: 'detectTime', label: 'Detect Time' },
    { key: 'uptime', label: 'Uptime', sortable: true },
  ];

  const bfdTab = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={Zap}
          title="Total Sessions"
          value={bfdSessions.length}
          color="primary"
        />
        <StatCard
          icon={Activity}
          title="Active Sessions"
          value={bfdSessions.filter((s) => s.state === 'Up').length}
          color="success"
        />
        <StatCard
          icon={AlertCircle}
          title="Down Sessions"
          value={bfdSessions.filter((s) => s.state === 'Down').length}
          color="danger"
        />
      </div>

      {bfdSessions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Zap className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>No BFD sessions configured for this edge</p>
        </div>
      ) : (
        <DataTable columns={bfdColumns} data={bfdSessions} searchPlaceholder="Search BFD sessions..." />
      )}
    </div>
  );

  const tabs: Tab[] = [
    { id: 'bgp', label: 'BGP Status', icon: Server, content: bgpTab },
    { id: 'ospf', label: 'OSPF Status', icon: NetworkIcon, content: ospfTab },
    { id: 'bfd', label: 'BFD Status', icon: Zap, content: bfdTab },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Routing</h1>
          <p className="text-gray-600 mt-1">Backbone control plane - BGP, OSPF, and BFD status</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedEdge}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedEdge(e.target.value)}
            className="input-base w-64"
          >
            <option value="">Select Edge Device</option>
            {edges.map((edge) => (
              <option key={edge.id} value={edge.id}>
                {edge.devName || edge.devSerNum} ({edge.devLocation || 'Unknown'})
              </option>
            ))}
          </select>
          <button 
            onClick={fetchRoutingData} 
            disabled={loading || !selectedEdge}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Edge Selection Notice */}
      {!selectedEdge ? (
        <div className="flex flex-col items-center justify-center py-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Server className="w-16 h-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Select an Edge Device</h3>
          <p className="text-gray-500">Choose an edge device from the dropdown above to view routing information</p>
        </div>
      ) : (
        <div className="card">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <Server className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Viewing Routing Information for:</p>
                <p className="text-sm text-blue-700 mt-1">
                  {selectedEdgeName}
                  {selectedEdgeData?.devSerNum && ` (${selectedEdgeData.devSerNum})`}
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
              <span className="ml-3 text-gray-600">Loading routing data...</span>
            </div>
          ) : (
            <Tabs tabs={tabs} />
          )}
        </div>
      )}
    </div>
  );
};

export default Routing;
